import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser, updateProfile, changePassword } from '../../store/slices/authSlice';
import { useToast } from '../Toast';
import AnnouncementDialogs from '../AnnouncementDialogs';
import './Layout.css';
import logo from '../../assets/images/HAIA_logo.png';

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, isLoading, error } = useSelector((state) => state.auth);
  const location = useLocation();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [profileData, setProfileData] = useState({
    email: user?.cmEmail || '',
    nickname: user?.nickname || '',
    cmLoginId: user?.cmLoginId || '',
    cmNameStaff: user?.cmNameStaff || '',
    cmTel: user?.cmTel || '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const mustChangePassword = user?.cmMustChangePassword === 1;

  const rawAccountType = user?.cmAccountType;
  const isIoyou = rawAccountType === 'ioyou';
  const isFullAccess = rawAccountType === 'admin' || isIoyou;
  const isCouponPage = location.pathname.startsWith('/coupons');

  const handleLogout = () => {
    dispatch(logoutUser()).then(() => {
      navigate('/login');
    });
  };

  const handleUserClick = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const handleOpenProfile = () => {
    setProfileData({
      email: user?.cmEmail || '',
      nickname: user?.nickname || '',
      cmLoginId: user?.cmLoginId || '',
      cmNameStaff: user?.cmNameStaff || '',
      cmTel: user?.cmTel || '',
    });
    setShowProfileModal(true);
    setShowUserDropdown(false);
  };

  const handleOpenPassword = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordError('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowPasswordModal(true);
    setShowUserDropdown(false);
  };

  const handleClosePasswordModal = () => {
    if (mustChangePassword) {
      toast.warning('デフォルトパスワードを変更して、サービスの利用を続けてください。');
      return;
    }
    setShowPasswordModal(false);
  };

  useEffect(() => {
    if (mustChangePassword) {
      setShowPasswordModal(true);
      toast.warning('デフォルトパスワードを変更して、サービスの利用を続けてください。');
    }
  }, [mustChangePassword]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (user) {
      setProfileData({
        email: user?.cmEmail || '',
        nickname: user?.nickname || '',
        cmLoginId: user?.cmLoginId || '',
        cmNameStaff: user?.cmNameStaff || '',
        cmTel: user?.cmTel || '',
      });
    }
  }, [user]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await dispatch(
        updateProfile({
          cmLoginId: profileData.cmLoginId,
          cmNameStaff: profileData.cmNameStaff,
          cmTel: profileData.cmTel,
        })
      ).unwrap();
      toast.success(result.message || 'プロフィールが正常に更新されました');
      setShowProfileModal(false);
    } catch (error) {
      toast.error(error || 'プロフィールの更新に失敗しました');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('新しいパスワードが一致しません');
      return;
    }

    const pwErrors = [];
    if (passwordData.newPassword.length < 8) pwErrors.push('8文字以上');
    if (!/[A-Z]/.test(passwordData.newPassword)) pwErrors.push('大文字1文字以上');
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(passwordData.newPassword)) pwErrors.push('特殊文字1文字以上');
    if (pwErrors.length > 0) {
      setPasswordError('パスワードの要件: ' + pwErrors.join('、'));
      return;
    }

    try {
      const result = await dispatch(changePassword({
        oldpassword: passwordData.currentPassword,
        newpassword: passwordData.newPassword,
        newpassword_confirmation: passwordData.confirmPassword,
      })).unwrap();
      toast.success(result.message || 'パスワードが正常に変更されました');
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      try {
        await dispatch(logoutUser()).unwrap();
      } finally {
        navigate('/login', { replace: true });
      }
    } catch (error) {
      setPasswordError(error || 'パスワードの変更に失敗しました');
    }
  };

  const fullMenuItems = [
    {
      category: '統計',
      items: [
        { path: '/dashboard', label: 'ダッシュボード', icon: 'dashboard' },
      ],
    },
    {
      category: 'メニュー',
      items: [
        { path: '/customers', label: 'クライアント', icon: 'customers' },
        { path: '/coupons', label: 'クーポン', icon: 'coupon' },
        { path: '/coupon-types', label: '種類生成', icon: 'couponType' },
        { path: '/announcements', label: 'お知らせ', icon: 'announcement' },
        ...(isIoyou
          ? [{ path: '/users', label: 'Healthtronユーザー', icon: 'healthtronUsers' }]
          : []),
      ],
    },
  ];

  const clientMenuItems = [
    {
      category: 'メニュー',
      items: [
        { path: '/coupons', label: 'クーポン', icon: 'coupon' },
      ],
    },
  ];

  const menuItems = isFullAccess ? fullMenuItems : clientMenuItems;

  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'dashboard':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="layout__menu-icon-svg">
  <path fillRule="evenodd" d="M2.25 13.5a8.25 8.25 0 0 1 8.25-8.25.75.75 0 0 1 .75.75v6.75H18a.75.75 0 0 1 .75.75 8.25 8.25 0 0 1-16.5 0Z" clipRule="evenodd" />
  <path fillRule="evenodd" d="M12.75 3a.75.75 0 0 1 .75-.75 8.25 8.25 0 0 1 8.25 8.25.75.75 0 0 1-.75.75h-7.5a.75.75 0 0 1-.75-.75V3Z" clipRule="evenodd" />
</svg>
        );
      case 'history':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="layout__menu-icon-svg">
            <path d="M21 6.375c0 2.692-4.03 4.875-9 4.875S3 9.067 3 6.375 7.03 1.5 12 1.5s9 2.183 9 4.875Z" />
            <path d="M12 12.75c2.685 0 5.19-.586 7.078-1.609a8.283 8.283 0 0 0 1.897-1.384c.016.121.025.244.025.368C21 12.817 16.97 15 12 15s-9-2.183-9-4.875c0-.124.009-.247.025-.368a8.285 8.285 0 0 0 1.897 1.384C6.809 12.164 9.315 12.75 12 12.75Z" />
            <path d="M12 16.5c2.685 0 5.19-.586 7.078-1.609a8.282 8.282 0 0 0 1.897-1.384c.016.121.025.244.025.368 0 2.692-4.03 4.875-9 4.875s-9-2.183-9-4.875c0-.124.009-.247.025-.368a8.284 8.284 0 0 0 1.897 1.384C6.809 15.914 9.315 16.5 12 16.5Z" />
            <path d="M12 20.25c2.685 0 5.19-.586 7.078-1.609a8.282 8.282 0 0 0 1.897-1.384c.016.121.025.244.025.368 0 2.692-4.03 4.875-9 4.875s-9-2.183-9-4.875c0-.124.009-.247.025-.368a8.284 8.284 0 0 0 1.897 1.384C6.809 19.664 9.315 20.25 12 20.25Z" />
          </svg>
        );
      case 'customers':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="layout__menu-icon-svg">
  <path d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z" />
</svg>
        );
      case 'coupon':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="layout__menu-icon-svg">
  <path fillRule="evenodd" d="M1.5 6.375c0-1.036.84-1.875 1.875-1.875h17.25c1.035 0 1.875.84 1.875 1.875v3.026a.75.75 0 0 1-.375.65 2.249 2.249 0 0 0 0 3.898.75.75 0 0 1 .375.65v3.026c0 1.035-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 0 1 1.5 17.625v-3.026a.75.75 0 0 1 .374-.65 2.249 2.249 0 0 0 0-3.898.75.75 0 0 1-.374-.65V6.375Zm15-1.125a.75.75 0 0 1 .75.75v.75a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75Zm.75 4.5a.75.75 0 0 0-1.5 0v.75a.75.75 0 0 0 1.5 0v-.75Zm-.75 3a.75.75 0 0 1 .75.75v.75a.75.75 0 0 1-1.5 0v-.75a.75.75 0 0 1 .75-.75Zm.75 4.5a.75.75 0 0 0-1.5 0V18a.75.75 0 0 0 1.5 0v-.75ZM6 12a.75.75 0 0 1 .75-.75H12a.75.75 0 0 1 0 1.5H6.75A.75.75 0 0 1 6 12Zm.75 2.25a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3Z" clipRule="evenodd" />
</svg>
        );
      case 'couponType':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="layout__menu-icon-svg">
            <path fillRule="evenodd" d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875Zm5.845 17.03a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72V12a.75.75 0 0 0-1.5 0v4.19l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3Z" clipRule="evenodd" />
            <path d="M14.25 5.25a5.23 5.23 0 0 0-1.279-3.434 9.768 9.768 0 0 1 6.963 6.963A5.23 5.23 0 0 0 16.5 7.5h-1.875a.375.375 0 0 1-.375-.375V5.25Z" />
          </svg>
        );
      case 'announcement':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6">
            <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clip-rule="evenodd" />
          </svg>

        );
      case 'healthtronUsers':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="layout__menu-icon-svg">
            <path d="M5.25 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM2.25 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM18.75 7.5a.75.75 0 0 0-1.5 0v2.25H15a.75.75 0 0 0 0 1.5h2.25v2.25a.75.75 0 0 0 1.5 0v-2.25H21a.75.75 0 0 0 0-1.5h-2.25V7.5Z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="layout">
      {!sidebarCollapsed && (
        <div 
          className="layout__overlay" 
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      <aside className={`layout__sidebar ${sidebarCollapsed ? 'layout__sidebar--collapsed' : ''}`}>
        <div className="layout__logo">
          <button 
            className="layout__sidebar-close"
            onClick={() => setSidebarCollapsed(true)}
          >
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <nav className="layout__nav">
          {menuItems.map((group, groupIndex) => (
            <div key={groupIndex} className="layout__menu-group">
              <div className="layout__menu-category">{group.category}</div>
              <ul className="layout__menu-list">
                {group.items.map((item) => (
                  <li key={item.path} className="layout__menu-item">
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `layout__menu-link ${isActive ? 'layout__menu-link--active' : ''}`
                      }
                    >
                      <span className="layout__menu-icon">{renderIcon(item.icon)}</span>
                      <span className="layout__menu-label" title={item.label}>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="layout__logout">
          <button className="layout__logout-btn" onClick={handleLogout}>
            <span className="layout__logout-text">ログアウト</span>
          </button>
        </div>
      </aside>

      <div className="layout__main">
        <header className="layout__header">
          <div className="layout__header-left">
            <div className="layout__header-logo">
              <img src={logo} alt="ハクジュ プラザ" className="layout__header-logo-img" />
            </div>
            <button
              className="layout__hamburger"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              <span className="layout__hamburger-line"></span>
              <span className="layout__hamburger-line"></span>
              <span className="layout__hamburger-line"></span>
            </button>
          </div>

          <div className="layout__header-right">
            <div className="layout__user-wrapper">
              <div className="layout__user" onClick={handleUserClick}>
                <span className="layout__user-name">{user?.cmLoginId || user?.cmEmail || 'ユーザー'}</span>
                <div className="layout__user-avatar">
                  <svg viewBox="0 0 24 24" className="layout__user-avatar-svg">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                  </svg>
                </div>
              </div>

              {showUserDropdown && (
                <>
                  <div className="layout__dropdown-overlay" onClick={() => setShowUserDropdown(false)} />
                  <div className="layout__user-dropdown">
                    <button className="layout__dropdown-item" onClick={handleOpenProfile}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd" />
                      </svg>
                      プロフィール編集
                    </button>
                    <button className="layout__dropdown-item" onClick={handleOpenPassword}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clipRule="evenodd" />
                      </svg>
                      パスワード変更
                    </button>
                    <button className="layout__dropdown-item layout__dropdown-item--logout" onClick={handleLogout}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 0 0 6 5.25v13.5a1.5 1.5 0 0 0 1.5 1.5h6a1.5 1.5 0 0 0 1.5-1.5V15a.75.75 0 0 1 1.5 0v3.75a3 3 0 0 1-3 3h-6a3 3 0 0 1-3-3V5.25a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3V9A.75.75 0 0 1 15 9V5.25a1.5 1.5 0 0 0-1.5-1.5h-6Zm10.72 4.72a.75.75 0 0 1 1.06 0l3 3a.75.75 0 0 1 0 1.06l-3 3a.75.75 0 1 1-1.06-1.06l1.72-1.72H9a.75.75 0 0 1 0-1.5h10.94l-1.72-1.72a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                      </svg>
                      ログアウト
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main className={`layout__content ${isCouponPage ? 'layout__content--coupon' : ''}`}>
          <div className="layout__content-round">
            <div className="layout__content-inner">
              {children}
            </div>
          </div>
        </main>
      </div>

      {showProfileModal && (
        <div className="layout__modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="layout__modal" onClick={(e) => e.stopPropagation()}>
            <div className="layout__modal-header">
              <h2 className="layout__modal-title">プロフィール編集</h2>
              <button className="layout__modal-close" onClick={() => setShowProfileModal(false)}>×</button>
            </div>
            <form className="layout__modal-form" onSubmit={handleProfileSubmit}>
              <div className="layout__modal-field">
                <label className="layout__modal-label">メールアドレス</label>
                <input
                  type="email"
                  className="layout__modal-input layout__modal-input--readonly"
                  value={profileData.email}
                  readOnly
                  disabled
                />
              </div>
              <div className="layout__modal-field">
                <label className="layout__modal-label">ログインID</label>
                <input
                  type="text"
                  className="layout__modal-input"
                  value={profileData.cmLoginId}
                  onChange={(e) =>
                    setProfileData({ ...profileData, cmLoginId: e.target.value })
                  }
                  disabled={isLoading}
                />
              </div>
              <div className="layout__modal-field">
                <label className="layout__modal-label">氏名</label>
                <input
                  type="text"
                  className="layout__modal-input"
                  value={profileData.cmNameStaff}
                  onChange={(e) =>
                    setProfileData({ ...profileData, cmNameStaff: e.target.value })
                  }
                  disabled={isLoading}
                />
              </div>
              <div className="layout__modal-field">
                <label className="layout__modal-label">電話番号</label>
                <input
                  type="tel"
                  className="layout__modal-input"
                  value={profileData.cmTel}
                  onChange={(e) =>
                    setProfileData({ ...profileData, cmTel: e.target.value })
                  }
                  disabled={isLoading}
                />
              </div>
              <div className="layout__modal-actions">
                <button type="button" className="layout__modal-btn layout__modal-btn--cancel" onClick={() => setShowProfileModal(false)}>
                  キャンセル
                </button>
                <button type="submit" className="layout__modal-btn layout__modal-btn--submit" disabled={isLoading}>
                  {isLoading ? '更新中...' : '更新'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div className="layout__modal-overlay" onClick={handleClosePasswordModal}>
          <div className="layout__modal" onClick={(e) => e.stopPropagation()}>
            <div className="layout__modal-header">
              <h2 className="layout__modal-title">パスワード変更</h2>
              <button className="layout__modal-close" onClick={handleClosePasswordModal}>×</button>
            </div>
            <form className="layout__modal-form" onSubmit={handlePasswordSubmit}>
              <div className="layout__modal-field">
                <label className="layout__modal-label">現在のパスワード</label>
                <div className="layout__modal-input-wrapper">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="layout__modal-input layout__modal-input--password"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value.replace(/^\s+/, ''),
                      })
                    }
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    className="layout__modal-eye-btn"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    tabIndex={-1}
                  >
                    {showCurrentPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                        <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                        <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                        <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="layout__modal-field">
                <label className="layout__modal-label">新しいパスワード</label>
                <div className="layout__modal-input-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className="layout__modal-input layout__modal-input--password"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        newPassword: e.target.value.replace(/^\s+/, ''),
                      })
                    }
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    className="layout__modal-eye-btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    tabIndex={-1}
                  >
                    {showNewPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                        <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                        <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                        <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="layout__modal-field">
                <label className="layout__modal-label">新しいパスワード（確認）</label>
                <div className="layout__modal-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="layout__modal-input layout__modal-input--password"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        confirmPassword: e.target.value.replace(/^\s+/, ''),
                      })
                    }
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    className="layout__modal-eye-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                        <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                        <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                        <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              {passwordError && (
                <div className="layout__modal-error">{passwordError}</div>
              )}
              <div className="layout__modal-actions">
                <button type="button" className="layout__modal-btn layout__modal-btn--cancel" onClick={handleClosePasswordModal}>
                  キャンセル
                </button>
                <button type="submit" className="layout__modal-btn layout__modal-btn--submit" disabled={isLoading}>
                  {isLoading ? '変更中...' : '変更'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AnnouncementDialogs />
    </div>
  );
};

export default Layout;
